import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { messageFrom } from '@bp-files/client';
import { Eyebrow, token } from '@bp-files/ui';
import { getSignature, removeSignature, saveSignature, type SignatureBody } from '@/lib/api';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';
import { StoredSignature } from './StoredSignature';
import { SIGNATURE_COPY } from './signature.copy';

/**
 * `/settings/signature` — a person's own signature (USR-13, SPEC §9.3).
 *
 * **An account screen, not a sixth surface.** §9.3's rule for the credentials
 * screen applies unchanged: this is reached from the account menu and from
 * nowhere else, because `surfaces.ts` holds the five things the product is
 * about and this one is about the person whose menu it is.
 *
 * **The statement shown is the one the server sent, always.** Rendering a copy
 * from this workspace would make the acceptance a record of whatever the
 * browser happened to display, which is not a fact anybody can check later —
 * and a stale bundle would record acceptance of wording already replaced. The
 * version travels back with the save, and a mismatch is a 409 the screen turns
 * into "read it again".
 *
 * **Drawing and agreeing are one step.** Both are required and neither is
 * stored without the other, so Save is inert until both hold and says which one
 * is missing rather than leaving a disabled button to be guessed at.
 */

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; data: SignatureBody }
  | { kind: 'failed'; message: string };

/**
 * Where to return once a signature is drawn.
 *
 * Only the PDF viewer's sign button sets this — the account menu's own space
 * to this screen carries no state, and rightly renders no back space, because
 * there is no document to send somebody back to.
 */
interface SignatureScreenState {
  from?: string;
}

export function SignatureScreen() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const from = (useLocation().state as SignatureScreenState | null)?.from;

  const [hasInk, setHasInk] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const padRef = useRef<SignaturePadHandle>(null);

  useEffect(() => {
    const controller = new AbortController();

    getSignature(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setState({ kind: 'ready', data });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setState({ kind: 'failed', message: messageFrom(caught, SIGNATURE_COPY.errors.load) });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  const onInkChange = useCallback((present: boolean) => {
    setHasInk(present);
  }, []);

  async function save(clickwrapVersion: number) {
    const ink = padRef.current?.ink();
    if (ink === undefined) return;

    setBusy(true);
    setActionError(undefined);

    try {
      await saveSignature(ink, clickwrapVersion);
      padRef.current?.clear();
      setAccepted(false);
      // Re-read rather than patching state from the response: the row carries
      // `createdAt` from the first save, which only the server knows.
      setReloadToken((current) => current + 1);
    } catch (caught) {
      // The API's own words wherever there are any — a 409 tells the person the
      // agreement changed, which nothing on this screen could work out.
      setActionError(messageFrom(caught, SIGNATURE_COPY.errors.save));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setActionError(undefined);

    try {
      await removeSignature();
      setReloadToken((current) => current + 1);
    } catch (caught) {
      setActionError(messageFrom(caught, SIGNATURE_COPY.errors.remove));
    } finally {
      setBusy(false);
    }
  }

  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-sm" style={{ color: token('bp-text-secondary') }}>
        {SIGNATURE_COPY.states.loading}
      </p>
    );
  }

  if (state.kind === 'failed') {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm" style={{ color: token('bp-danger') }}>
          {state.message}
        </p>
        <button
          type="button"
          onClick={() => {
            setState({ kind: 'loading' });
            setReloadToken((current) => current + 1);
          }}
          className="rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-elevated"
          style={{ borderColor: token('bp-border-strong') }}
        >
          {SIGNATURE_COPY.errors.retry}
        </button>
      </div>
    );
  }

  const { signature, clickwrap } = state.data;
  const stale = signature !== null && signature.clickwrapVersion !== clickwrap.version;
  const canSave = hasInk && accepted && !busy;

  return (
    <section className="flex max-w-2xl flex-col gap-6">
      {from === undefined ? null : (
        <Link
          to={from}
          className="inline-flex w-fit items-center gap-1 text-xs transition-colors hover:text-body"
          style={{ color: token('bp-text-secondary') }}
        >
          <BackIcon />
          {SIGNATURE_COPY.backToDocument}
        </Link>
      )}

      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold">{SIGNATURE_COPY.title}</h1>
        <p className="text-sm" style={{ color: token('bp-text-secondary') }}>
          {SIGNATURE_COPY.intro}
        </p>
      </header>

      {signature !== null ? (
        <StoredSignature signature={signature} busy={busy} onRemove={() => void remove()} />
      ) : null}

      {stale ? (
        <p className="text-sm" style={{ color: token('bp-amber') }}>
          {SIGNATURE_COPY.clickwrap.stale}
        </p>
      ) : null}

      <div className="flex flex-col gap-3">
        <Eyebrow>{SIGNATURE_COPY.pad.label}</Eyebrow>
        <SignaturePad ref={padRef} onInkChange={onInkChange} />
      </div>

      <div className="flex flex-col gap-3">
        <Eyebrow>{SIGNATURE_COPY.clickwrap.heading}</Eyebrow>
        {/*
          The server's text, rendered as it arrived. `whitespace-pre-line` keeps
          its paragraphs without this screen reformatting a legal statement.
        */}
        <p
          className="text-sm whitespace-pre-line"
          style={{ color: token('bp-text-secondary') }}
          data-testid="clickwrap-text"
        >
          {clickwrap.text}
        </p>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => {
              setAccepted(event.target.checked);
            }}
            className="mt-0.5"
          />
          <span>{SIGNATURE_COPY.clickwrap.accept}</span>
        </label>
      </div>

      {actionError === undefined ? null : (
        <p role="alert" className="text-sm" style={{ color: token('bp-danger') }}>
          {actionError}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!canSave}
          aria-busy={busy}
          onClick={() => void save(clickwrap.version)}
          className="rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          style={{ background: token('bp-blue'), color: token('bp-on-accent') }}
        >
          {busy
            ? SIGNATURE_COPY.save.working
            : signature === null
              ? SIGNATURE_COPY.save.action
              : SIGNATURE_COPY.save.replace}
        </button>

        {/*
          Why the button is inert, said rather than left to be guessed at. Ink
          first: it is the step people reach for, and naming the agreement while
          the pad is still empty would read as the wrong instruction.
        */}
        {!busy && !hasInk ? (
          <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
            {SIGNATURE_COPY.save.needsInk}
          </p>
        ) : null}
        {!busy && hasInk && !accepted ? (
          <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
            {SIGNATURE_COPY.save.needsAccept}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/** Lucide's `chevron-left`, hand-copied — the same glyph `App.tsx`'s own back button uses. */
function BackIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}
